import type { ICartGroup, ICartListResponse } from '@/api/types/cart';
import { ProviderType, ReportingCenter, type TrackingEvent } from '@/reporting';
import { generateEventId } from '@/reporting/utils/eventIdGenerator';
import { IS_CA } from '@/utils/constants';

export const CA_FRAME_LIST_DIAGNOSTIC_EVENT = 'CAFrameList_Diagnostic';

const DIAGNOSTIC_STORAGE_KEY = 'zl-ca-frame-list-diagnostic:v1';
const DIAGNOSTIC_VERSION = 1;
const DIAGNOSTIC_TTL_MS = 10 * 60 * 1_000;

type CartLocation = 'pending_lens_list' | 'list' | 'invalid_list' | 'not_found';

interface DiagnosticStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CaFrameListDiagnosticContext {
  version: 1;
  diagnostic_flow_id: string;
  target_cart_id: number;
  created_at: number;
  expires_at: number;
  cart_entry_count: number;
  navigation_type?: string;
  request_started_at?: number;
  reported_stage_keys: string[];
}

interface CreateCaFrameListDiagnosticClientOptions {
  isCa: boolean;
  getStorage: () => DiagnosticStorage | undefined;
  now: () => number;
  createFlowId: () => string;
  getNavigationType: () => string;
  track: (event: TrackingEvent, providers: ProviderType[]) => void;
}

type CartListData = NonNullable<ICartListResponse['data']>;

function isValidContext(value: unknown): value is CaFrameListDiagnosticContext {
  if (!value || typeof value !== 'object') return false;

  const context = value as Partial<CaFrameListDiagnosticContext>;
  return (
    context.version === DIAGNOSTIC_VERSION &&
    typeof context.diagnostic_flow_id === 'string' &&
    context.diagnostic_flow_id.length > 0 &&
    typeof context.target_cart_id === 'number' &&
    Number.isFinite(context.target_cart_id) &&
    typeof context.created_at === 'number' &&
    typeof context.expires_at === 'number' &&
    typeof context.cart_entry_count === 'number' &&
    Array.isArray(context.reported_stage_keys)
  );
}

function getCartItems(list: unknown): Array<{ cart_id?: number }> {
  if (!Array.isArray(list)) return [];

  return list.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];

    const cartEntry = entry as { cart_id?: number; carts?: Array<{ cart_id?: number }> };
    if (Array.isArray(cartEntry.carts)) return cartEntry.carts;
    return [cartEntry];
  });
}

function hasCartId(list: unknown, targetCartId: number): boolean {
  return getCartItems(list).some((item) => Number(item.cart_id) === targetCartId);
}

function resolveCartLocation(data: CartListData | undefined, targetCartId: number): CartLocation {
  if (hasCartId(data?.pending_lens_list, targetCartId)) return 'pending_lens_list';
  if (hasCartId(data?.list, targetCartId)) return 'list';
  if (
    hasCartId(data?.can_not_sell_list, targetCartId) ||
    hasCartId(data?.can_not_buy_list, targetCartId)
  ) {
    return 'invalid_list';
  }
  return 'not_found';
}

export function createCaFrameListDiagnosticClient(
  options: CreateCaFrameListDiagnosticClientOptions,
) {
  const getStorage = (): DiagnosticStorage | undefined => {
    try {
      return options.getStorage();
    } catch {
      return undefined;
    }
  };

  const removeContext = () => {
    try {
      getStorage()?.removeItem(DIAGNOSTIC_STORAGE_KEY);
    } catch {
      // 诊断数据清理失败不能影响业务流程。
    }
  };

  const writeContext = (context: CaFrameListDiagnosticContext) => {
    try {
      getStorage()?.setItem(DIAGNOSTIC_STORAGE_KEY, JSON.stringify(context));
    } catch {
      // 浏览器禁用 Storage 时仅停止后续链路关联，不影响当前操作。
    }
  };

  const getContext = (): CaFrameListDiagnosticContext | null => {
    if (!options.isCa) return null;

    try {
      const rawContext = getStorage()?.getItem(DIAGNOSTIC_STORAGE_KEY);
      if (!rawContext) return null;

      const context: unknown = JSON.parse(rawContext);
      if (!isValidContext(context) || context.expires_at <= options.now()) {
        removeContext();
        return null;
      }
      return context;
    } catch {
      removeContext();
      return null;
    }
  };

  const track = (properties: Record<string, unknown>) => {
    if (!options.isCa) return;

    try {
      options.track(
        {
          eventName: CA_FRAME_LIST_DIAGNOSTIC_EVENT,
          properties: {
            ...properties,
            pub_env_name: 'ABtest',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch {
      // 诊断埋点失败不能影响商品加购或购物车业务。
    }
  };

  const start = (cartId: number): CaFrameListDiagnosticContext | null => {
    if (!options.isCa || !Number.isFinite(cartId)) return null;

    const eventAt = options.now();
    const context: CaFrameListDiagnosticContext = {
      version: DIAGNOSTIC_VERSION,
      diagnostic_flow_id: options.createFlowId(),
      target_cart_id: cartId,
      created_at: eventAt,
      expires_at: eventAt + DIAGNOSTIC_TTL_MS,
      cart_entry_count: 0,
      reported_stage_keys: ['add_later_success'],
    };

    writeContext(context);
    track({
      diagnostic_stage: 'add_later_success',
      diagnostic_flow_id: context.diagnostic_flow_id,
      target_cart_id: context.target_cart_id,
      add_cart_scene: '先加购后配镜',
      cart_entry_count: 0,
      navigation_type: 'navigate',
      event_at: eventAt,
    });
    return context;
  };

  const beginCartEntry = (): CaFrameListDiagnosticContext | null => {
    const context = getContext();
    if (!context) return null;

    const nextCartEntryCount = context.cart_entry_count + 1;
    let navigationType = 'navigate';
    if (nextCartEntryCount > 1) {
      try {
        navigationType = options.getNavigationType() || 'unknown';
      } catch {
        navigationType = 'unknown';
      }
    }

    const nextContext: CaFrameListDiagnosticContext = {
      ...context,
      cart_entry_count: nextCartEntryCount,
      navigation_type: navigationType,
      request_started_at: options.now(),
    };
    writeContext(nextContext);
    return nextContext;
  };

  const trackCartListResponse = (response: ICartListResponse) => {
    const context = getContext();
    if (!context || !context.request_started_at || context.cart_entry_count < 1) return;

    const stageKey = `cart_list_response:${context.cart_entry_count}`;
    if (context.reported_stage_keys.includes(stageKey)) return;

    const data = response.data;
    const targetLocation = resolveCartLocation(data, context.target_cart_id);
    const eventAt = options.now();

    track({
      diagnostic_stage: 'cart_list_response',
      diagnostic_flow_id: context.diagnostic_flow_id,
      target_cart_id: context.target_cart_id,
      cart_entry_count: context.cart_entry_count,
      navigation_type: context.navigation_type || 'unknown',
      request_duration_ms: Math.max(0, eventAt - context.request_started_at),
      response_code: response.code ?? 0,
      trace_id: response.trace_id || '',
      pending_lens_count: getCartItems(data?.pending_lens_list).length,
      checkout_count: getCartItems(data?.list).length,
      invalid_count: getCartItems(data?.can_not_sell_list ?? data?.can_not_buy_list).length,
      target_location: targetLocation,
      target_in_pending_list: targetLocation === 'pending_lens_list',
      event_at: eventAt,
    });

    writeContext({
      ...context,
      reported_stage_keys: [...context.reported_stage_keys, stageKey],
    });
  };

  const trackFrameListRendered = (
    pendingLensList: ICartGroup[],
    frameListRendered: boolean,
    targetItemRendered: boolean,
  ) => {
    const context = getContext();
    if (!context || context.cart_entry_count < 1) return;

    const stageKey = `frame_list_rendered:${context.cart_entry_count}`;
    if (context.reported_stage_keys.includes(stageKey)) return;

    const pendingLensCount = getCartItems(pendingLensList).length;
    const targetInStore = hasCartId(pendingLensList, context.target_cart_id);

    track({
      diagnostic_stage: 'frame_list_rendered',
      diagnostic_flow_id: context.diagnostic_flow_id,
      target_cart_id: context.target_cart_id,
      cart_entry_count: context.cart_entry_count,
      navigation_type: context.navigation_type || 'unknown',
      store_pending_lens_count: pendingLensCount,
      target_in_store: targetInStore,
      frame_list_rendered: frameListRendered,
      target_item_rendered: targetItemRendered,
      event_at: options.now(),
    });

    const nextContext = {
      ...context,
      reported_stage_keys: [...context.reported_stage_keys, stageKey],
    };
    if (targetInStore && targetItemRendered) {
      removeContext();
      return;
    }
    writeContext(nextContext);
  };

  return {
    start,
    beginCartEntry,
    trackCartListResponse,
    trackFrameListRendered,
    getContext,
  };
}

const productionClient = createCaFrameListDiagnosticClient({
  isCa: IS_CA,
  getStorage: () => (typeof window === 'undefined' ? undefined : window.sessionStorage),
  now: () => Date.now(),
  createFlowId: () => generateEventId(),
  getNavigationType: () => {
    if (typeof performance === 'undefined') return 'unknown';
    const navigationEntry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return navigationEntry?.type || 'unknown';
  },
  track: (event, providers) => ReportingCenter.getInstance().track(event, providers),
});

function runDiagnosticSafely<TResult>(operation: () => TResult, fallback: TResult): TResult {
  try {
    return operation();
  } catch {
    return fallback;
  }
}

export const startCaFrameListDiagnostic = (cartId: number) =>
  runDiagnosticSafely(() => productionClient.start(cartId), null);

export const beginCaFrameListCartEntry = () =>
  runDiagnosticSafely(() => productionClient.beginCartEntry(), null);

export const trackCaFrameListCartResponse = (response: ICartListResponse) =>
  runDiagnosticSafely(() => productionClient.trackCartListResponse(response), undefined);

export const trackCaFrameListRendered = (
  pendingLensList: ICartGroup[],
  frameListRendered: boolean,
  targetItemRendered: boolean,
) =>
  runDiagnosticSafely(
    () =>
      productionClient.trackFrameListRendered(
        pendingLensList,
        frameListRendered,
        targetItemRendered,
      ),
    undefined,
  );

export const getCaFrameListDiagnosticContext = () =>
  runDiagnosticSafely(() => productionClient.getContext(), null);
